import { useState, useEffect } from 'react'
import { Event, Language } from '@wellesley/openapi'
import { useTranslationConfigs } from '@wellesley/settings'
import { logger } from 'helpers/logger'
import TranslationControls from 'components/TranslationControls'
import { usePostApiV1EventsByEventIdTranslateMutation } from '../../api'

export const useEventTranslation = (event: Event) => {
  const [isShowingTranslation, setIsShowingTranslation] = useState(false)

  const { preferredLanguage, checkIfTranslatable } = useTranslationConfigs()

  const eventLang = event?.lang
  const eventTranslation = event?.translation

  const canTranslate = checkIfTranslatable(eventLang)

  const [translateRequest, { isLoading }] =
    usePostApiV1EventsByEventIdTranslateMutation()

  useEffect(() => {
    if (!eventTranslation) {
      setIsShowingTranslation(false)
    }
  }, [eventTranslation])

  const handleShowOriginal = () => setIsShowingTranslation(false)

  const handleTranslate = async () => {
    if (!eventTranslation) {
      try {
        const translation = await translateRequest({
          eventId: event.id,
          lang: preferredLanguage as Language,
        }).unwrap()

        if (translation) {
          setIsShowingTranslation(true)
        }
      } catch {
        logger.error('Failed to translate event')
      }
    } else {
      setIsShowingTranslation(true)
    }
  }

  const displayName = isShowingTranslation
    ? eventTranslation?.name || event.name
    : event.name

  const displayDescriptionHtml = isShowingTranslation
    ? eventTranslation?.descriptionHtml || event.descriptionHtml
    : event.descriptionHtml

  return {
    name: displayName,
    descriptionHtml: displayDescriptionHtml,
    TranslationControls: canTranslate && (
      <TranslationControls
        isShowingTranslation={isShowingTranslation}
        isLoading={isLoading}
        onTranslate={handleTranslate}
        onShowOriginal={handleShowOriginal}
      />
    ),
  }
}
